import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Share } from 'react-feather';
import { AlarmClock, Check } from 'lucide-react';
import { ArrowDown } from 'lucide-react';
import { useRef } from 'react';
import { Spin } from 'antd';
interface FeedbackScore {
  Score: number;
  Feedback: string;
}

interface KeyStrengths {
  ProblemSolving: string;
  Adaptability: string;
  Communication: string;
}

interface CultureFit {
  Collaboration: string;
  Inclusivity: string;
  Supportiveness: string;
}

interface ProfileAccuracy {
  Skills: number;
  Experience: number;
  Education: number;
}

interface ProfileConsistency {
  ExperienceAlignment: string;
  SkillValidation: string;
}

interface Evaluation {
  Relevance: FeedbackScore;
  TechnicalAccuracy: FeedbackScore;
  ClarityAndStructure: FeedbackScore;
  DepthOfKnowledge: FeedbackScore;
  OverallScore: number;
}

interface FeedbackResult {
  KeyStrengths: KeyStrengths;
  CultureFit: CultureFit;
  ProfileAccuracy: ProfileAccuracy;
  ProfileConsistency: ProfileConsistency;
  Evaluation: Evaluation;
  sessionId: string;
  totalDuration: number;
}

const ResultPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [keyStrengths, setKeyStrengths] = useState<KeyStrengths | null>(null);
  const [cultureFit, setCultureFit] = useState<CultureFit | null>(null);
  const [profileAccuracy, setProfileAccuracy] =
    useState<ProfileAccuracy | null>(null);
  const [profileConsistency, setProfileConsistency] =
    useState<ProfileConsistency | null>(null);
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [totalDuration, setTotalDuration] = useState<string>('0:00'); // Default format
  const [score, setScore] = useState<number>(0);
  const [label, setLabel] = useState<string>('Poor');
  const [color, setColor] = useState<string>('text-red-500');
  const [angle, setAngle] = useState<number>(-90);
  const [videoUrl, setVideoUrl] = useState<string>('');

  useEffect(() => {
    const fetchResults = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const resultResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/result/${sessionId}`
        );

        const feedbackResponse = await axios.get<{ result: FeedbackResult }>(
          `${process.env.REACT_APP_API_BASE_URL}/api/feedback/store/${sessionId}`
        );
        const feedbackData = feedbackResponse.data.result;

        // Set static values
        const minutes = Math.floor(feedbackData.totalDuration / 60);
        const seconds = feedbackData.totalDuration % 60;
        const formattedDuration = `${minutes}:${seconds
          .toString()
          .padStart(2, '0')}`;

        setTotalDuration(formattedDuration);

        setScore(feedbackData.Evaluation.OverallScore);
        setKeyStrengths(feedbackData.KeyStrengths);
        setCultureFit(feedbackData.CultureFit);
        setProfileAccuracy(feedbackData.ProfileAccuracy);
        setProfileConsistency(feedbackData.ProfileConsistency);
        setEvaluation(feedbackData.Evaluation);

        let attempts = 0;
        const maxAttempts = 4;
        const retryInterval = 60000;

        const checkRecording = async () => {
          try {
            const recordingResponse = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/recordings/${sessionId}`
            );

            if (recordingResponse.data?.recordingUrl) {
              console.log(
                '🎥 Recording ready:',
                recordingResponse.data.recordingUrl
              );
              setVideoUrl(recordingResponse.data.recordingUrl);
              return;
            }

            if (attempts < maxAttempts) {
              attempts++;
              console.log(`⏳ Waiting for recording... attempt ${attempts}`);
              setTimeout(checkRecording, retryInterval);
            } else {
              console.warn('⚠️ Max retries reached. Recording not available.');
              setVideoUrl('');
            }
          } catch (err) {
            console.error('Error fetching recording:', err);
            if (attempts < maxAttempts) {
              attempts++;
              setTimeout(checkRecording, retryInterval);
            } else {
              setVideoUrl('');
            }
          }
        };

        // Start recording polling
        checkRecording();
      } catch (err) {
        setError('Error fetching results. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [sessionId]);

  useEffect(() => {
    const newAngle = (score / 100) * 180 - 90; // Convert score to angle
    setAngle(newAngle);

    if (score < 20) {
      setLabel('Poor');
      setColor('text-red-500');
    } else if (score < 40) {
      setLabel('Fair');
      setColor('text-orange-500');
    } else if (score < 60) {
      setLabel('Good');
      setColor('text-yellow-500');
    } else if (score < 80) {
      setLabel('Very Good');
      setColor('text-green-400');
    } else {
      setLabel('Excellent');
      setColor('text-green-600');
    }
  }, [score]);

  const bottomRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const [isBottom, setIsBottom] = useState<boolean>(false);

  // Function to scroll to bottom
  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  // Function to scroll to top
  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Check if the user is near the bottom
  useEffect(() => {
    const handleScroll = () => {
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      setIsBottom(scrolled >= scrollableHeight - 50); // Adjust threshold if needed
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [isVideoLoading, setIsVideoLoading] = useState(true);

  return (
    <>
      <div className="flex h-screen w-full p-3">
        <div ref={topRef}></div>

        <div className="mainone">
          <div className="grid grid-cols-10 bg-white gap-8 min-h-screen h-screen">
            {/* Left Side */}

            <div className="col-span-3 pl-4  h-screen overflow-hidden">
              <div className="flex flex-col space-y-2 w-full">
                <div className="flex items-center justify-between mt-2">
                  <p className="text-lg font-semibold">Assessment Result</p>
                  {/* <button className="bg-first px-3 py-1 rounded-full text-xs text-black font-medium">
                    Back
                  </button>
                  <button className="bg-first px-3 py-1 rounded-full text-xs text-button">
                    Report Date: 30 Jan 2025
                  </button> */}
                </div>

                <div className="space-y-3 pt-4">
                  <div className="w-full h-[230px] relative">
                    {videoUrl ? (
                      <>
                        <video
                          src={videoUrl}
                          controls
                          className="w-full h-full object-cover rounded-lg"
                          onLoadStart={() => setIsVideoLoading(true)}
                          onCanPlay={() => setIsVideoLoading(false)}
                        />
                        {/* {isVideoLoading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 rounded-lg">
                            <Spin size="large" tip="Loading video..." />
                          </div>
                        )} */}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
                        {/* <Spin
                          size="large"
                          tip="Fetching your interview recording..."
                        /> */}
                      </div>
                    )}
                  </div>
                  {/* Profile Score Section */}
                  <div className="result flex flex-col w-full h-auto">
                    <div className="border border-borders flex flex-col rounded-xl p-3 space-y-3">
                      <div className="flex justify-center items-center space-x-2">
                        <p className="text-lg font-semibold">Profile Score</p>
                        <img src="/ques.png" className="w-3 h-3 object-cover" />
                      </div>

                      <div className="flex relative justify-center items-center">
                        <div className="relative flex justify-center items-center">
                          <img
                            src="/status.png"
                            alt="Status"
                            className="w-full max-w-xs h-auto"
                          />
                          <div className="absolute inset-0 flex justify-center items-center">
                            <div className="relative mt-8 mr-3 z-10 w-40 h-40 flex flex-col items-center justify-center bg-white rounded-full shadow-lg border-gray-200">
                              <span className="text-2xl font-bold">
                                {score}
                              </span>
                              <span
                                className={`text-sm font-semibold ${color}`}
                              >
                                {label}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Message Section */}
                      <div className="text-center">
                        <h2 className="text-sm font-semibold">Well Done!</h2>
                        <p className="text-xs leading-relaxed px-3">
                          You have completed your self-assessment. Your results
                          are insightful, but there’s always room for
                          improvement.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side */}
            <div
              className="col-span-7 pr-4 h-screen overflow-y-auto scrollbar-hide"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              <div>
                <div
                  className=" bg-black rounded-full fixed p-2 bottom-5 right-5 cursor-pointer z-50"
                  onClick={scrollToBottom}
                >
                  <ArrowDown className="text-white" />
                </div>

                <div className="w-full flex justify-between space-x-3 ">
                  <div className=" flex justify-center items-center  ">
                    <p className="text-sm  text-button">
                      <div className="flex justify-center p-2  items-center space-x-2 rounded-full border border-first hover:bg-orange-100 transition duration-300">
                        <AlarmClock />
                        <span className="text-[#022c22] font-bold">
                          {totalDuration}
                        </span>
                        <span className="text-gray-500">/ 10 minutes</span>
                      </div>
                    </p>
                  </div>
                  <div className=" flex justify-center items-center space-x-4 ">
                    <button className="flex items-center space-x-2 px-4 py-2 bg-white border  rounded-md  hover:bg-gray-100">
                      <Share className="w-5 h-5 text-black" />
                      <span className="text-black font-medium">Share</span>
                    </button>

                    <button className="px-6 py-2 bg-[#022c22] text-white font-semibold rounded-md shadow-sm hover:bg-[#022c22">
                      Retake Assessment
                    </button>
                  </div>
                </div>
              </div>

              {/* progress bar */}
              <div className=" mt-4 space-y-8">
                <div className=" grid grid-cols-5 gap-7">
                  <div className="col-span-2">
                    <div className="border border-borders w-full rounded-3xl p-4 space-y-6 ">
                      <div className=" flex justify-start">
                        <p className="text-lg font-semibold">
                          Profile Accuracy
                        </p>
                      </div>

                      {profileAccuracy ? (
                        Object.entries(profileAccuracy).map(([key, value]) => (
                          <div key={key} className="mb-4 space-y-6">
                            <div className=" flex justify-between">
                              <p className="text-sm font-semibold text-gray-700">
                                {key}
                              </p>

                              <p className="text-md font-semibold text-gray-900">
                                {value} %
                              </p>
                            </div>

                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${
                                  key === 'Skills'
                                    ? 'bg-yellow-500'
                                    : key === 'Experience'
                                    ? 'bg-green-500'
                                    : key === 'Education'
                                    ? 'bg-pink-500'
                                    : 'bg-blue-500'
                                }`}
                                style={{ width: `${value}%` }}
                              ></div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p>Loading...</p>
                      )}
                    </div>
                  </div>

                  <div className="col-span-3">
                    <div className="border border-gray-300 w-full h-auto space-y-8 rounded-3xl p-6">
                      <p className="text-lg font-semibold text-gray-800">
                        Profile Consistency
                      </p>

                      {profileConsistency ? (
                        <div className="space-y-3">
                          {/* Experience Alignment Section */}
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0 bg-round rounded-full">
                              <img
                                src="/bag.png"
                                alt="Experience Icon"
                                className="w-8 h-8"
                              />
                            </div>
                            <div className="flex flex-col text-record ">
                              <p className="text-md font-semibold text-gray-800">
                                Experience Alignment:{' '}
                                <span className="text-sm font-thin text-gray-600">
                                  {profileConsistency.ExperienceAlignment}
                                </span>
                              </p>
                            </div>
                          </div>

                          {/* Skill Validation Section */}
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              <img
                                src="/tick.png"
                                alt="Skill Validation Icon"
                                className="w-8 h-8"
                              />
                            </div>
                            <div className="flex flex-col text-record ">
                              <p className="text-md font-semibold text-gray-800">
                                Skill Validation:{' '}
                                <span className="text-sm font-thin text-gray-600">
                                  {profileConsistency.SkillValidation}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Loading data...</p>
                      )}
                    </div>
                  </div>

                  {/* Feedback Validation Section */}
                </div>

                {/* Evaluation section */}

                <div className=" w-full ">
                  <div className="">
                    <div className="flex justify-center items-center mb-6 ">
                      <div className=" w-auto bg-gray-200 rounded-md">
                        <p className="text-lg font-semibold  text-gray-600  px-4 py-2">
                          Feedbacks
                        </p>
                      </div>
                    </div>
                    {evaluation ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {Object.entries(evaluation).map(([key, value]) => {
                          if (key === 'OverallScore') return null; // Skip OverallScore if present
                          return (
                            <div
                              key={key}
                              className="border border-gray-300 rounded-3xl p-6 shadow-sm hover:shadow-md transition"
                            >
                              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                              </h3>
                              <div className="w-full bg-gray-200 rounded-full h-1 mb-4">
                                <div
                                  className="bg-blueai h-1 rounded-full"
                                  style={{ width: `${value.Score}%` }}
                                ></div>
                              </div>
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8  rounded-full flex items-center justify-center">
                                    <img
                                      src="/chat-icon.png"
                                      alt="Chat Icon"
                                      className="w-6 h-6"
                                    />
                                  </div>
                                </div>
                                <div className="text-record">
                                  <p className="text-sm font-thin text-gray-600">
                                    <span className=" text -sm font-semibold text-gray-800">
                                      Feedback:{' '}
                                    </span>
                                    {value.Feedback}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Loading data...</p>
                    )}
                  </div>
                </div>

                {/* key strength */}


                <div className=" w-full">

                  <div className="flex justify-center items-center mb-6 ">
                    <div className=" w-auto bg-gray-200 rounded-md">
                      <p className="text-lg font-semibold  text-gray-600  px-4 py-2">
                        Values
                      </p>
                    </div>
                  </div>

                  <div className="p-6 border border-gray-300 rounded-3xl">
                    <div className=" flex flex-col flex-shrink w-full justify-center items-center">
                      <p className="text-lg font-semibold text-gray-800  rounded-md">
                        {' '}
                        Key Strength
                      </p>
                      <img
                        src="/wavekey.png"
                        className=" object-cover w-1/2 "
                      ></img>
                    </div>
                    {keyStrengths ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {Object.entries(keyStrengths).map(
                          ([key, value], index) => (
                            <div
                              key={key}
                              className="flex items-start space-x-2"
                            >
                              {/* Number Circle */}
                              <div className="flex-shrink-0 w-6 h-6 bg-ui-green text-white font-semibold rounded-full flex items-center justify-center">
                                {index + 1}
                              </div>
                              {/* Key Strength Details */}
                              <div className=" text-record  space-y-1 ">
                                <p className="text-lg font-semibold text-gray-800">
                                  {key.replace(/([A-Z])/g, ' $1').trim()}
                                </p>
                                <p className="text-sm font-thin leading-6 text-gray-600">
                                  {value}
                                </p>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Loading data...</p>
                    )}
                  </div>
                </div>

                {/* key strength */}

                <div>
                  <div className=" w-full">
                    <div className="p-6 border border-gray-300 rounded-3xl space-y-4">
                      <div className=" flex flex-col flex-shrink w-full justify-center items-center">
                        <p className="text-lg font-semibold text-gray-800  rounded-md">
                          Culture fit
                        </p>
                      </div>
                      {cultureFit ? (
                        <div className="grid grid-cols-1  gap-6">
                          {Object.entries(cultureFit).map(
                            ([key, value], index) => (
                              <div
                                key={key}
                                className="flex items-start space-x-4"
                              >
                                {/* Number Circle */}
                                <img
                                  src="/tick-culture.png"
                                  className="w-6 h-6"
                                ></img>
                                {/* Key Strength Details */}
                                <div className="flex flex-col w-11/12 text-record ">
                                  <p className="text-lg font-semibold text-gray-800">
                                    {key.replace(/([A-Z])/g, ' $1').trim()}:{' '}
                                    <span className="text-sm font-thin text-gray-600">
                                      {value}
                                    </span>
                                  </p>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Loading data...</p>
                      )}
                    </div>

                    <br />

                    <div ref={bottomRef}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResultPage;
